import type { LegalRun } from '../models/ast';
import { measureLineRects, measureZeroWidthAnchorRect, type LineRect } from './RectsWrapper';
import type { ResolvedStyle } from './FontResolver';
import { getFontMetrics } from './FontMetrics';

export interface GraphemeMapEntry { lineIndex: number; x: number }
export interface TextMeasureResult {
  rectsByLine: LineRect[];
  graphemeMap: Record<string, GraphemeMapEntry>;
}

function segmentGraphemes(text: string): string[] {
  if ((Intl as any)?.Segmenter) {
    const seg = new (Intl as any).Segmenter(undefined, { granularity: 'grapheme' });
    return Array.from(seg.segment(text)).map((s: any) => s.segment);
  }
  // Fallback: naive split (not perfect for complex scripts but acceptable for v1 tests)
  return Array.from(text);
}

export interface RunWithStyle { run: LegalRun; style: ResolvedStyle }
export interface MeasureRunsOptions {
  granularity?: 'full' | 'coarse';
  // future: hyphenator?: HyphenationService
}

export function measureRuns(
  container: HTMLElement,
  items: RunWithStyle[],
  options: MeasureRunsOptions = { granularity: 'full' }
): TextMeasureResult {
  // Build a DOM fragment that mirrors the runs with resolved styles
  const frag = document.createDocumentFragment();
  const spans: HTMLSpanElement[] = [];
  for (const { run, style } of items) {
    const span = document.createElement('span');
    // Apply resolved style
    span.style.fontFamily = style.fontFamily;
    span.style.fontSize = `${Math.max(1, Math.round(style.fontSizePt * (96 / 72)))}px`;
    span.style.fontWeight = String(style.fontWeight);
    span.style.fontStyle = style.fontStyle;
    span.style.color = style.colorHex;
    if (run.marks?.underline) span.style.textDecoration = 'underline';

    if (run.text && run.text.length > 0) {
      span.textContent = run.text;
    } else {
      // Inline atomic nodes (e.g., footnote anchors) → zero-width probe
      span.style.display = 'inline-block';
      span.style.width = '0px';
    }
    frag.appendChild(span);
    spans.push(span);
  }
  container.innerHTML = '';
  container.appendChild(frag);

  // Global line rectangles for snapping
  const global = document.createRange();
  const first = spans[0]?.firstChild || spans[0];
  const last = spans[spans.length - 1]?.firstChild || spans[spans.length - 1];
  if (!first || !last) return { rectsByLine: [], graphemeMap: {} };
  try {
    global.setStart(first as Node, 0);
  } catch {}
  try {
    const endLen = (last as Text)?.length ?? 0;
    global.setEnd(last as Node, endLen);
  } catch {}
  const origin = container.getBoundingClientRect();
  const rectsRaw = measureLineRects(global);
  const baseStyle = items[0]?.style as ResolvedStyle | undefined;
  const metrics = baseStyle ? getFontMetrics(baseStyle) : { ascentPx: Math.round(((items[0]?.style.fontSizePt ?? 12) * (96 / 72)) * 0.8), descentPx: 0, lineGapPx: 0 } as any;
  const rectsByLine = rectsRaw.map(r => {
    const x = r.x - origin.left;
    const y = r.y - origin.top;
    return {
      x: x < 0 && x > -2 ? 0 : x,
      y: y < 0 && y > -2 ? 0 : y,
      width: r.width,
      height: r.height,
      baseline: y + Math.round(metrics.ascentPx),
    } as LineRect;
  });

  const graphemeMap: Record<string, GraphemeMapEntry> = {};

  if (options.granularity === 'coarse') {
    // Coarse: anchor at run start only
    rectsByLine; // noop to avoid lint unused
    for (let i = 0; i < items.length; i++) {
      const { run } = items[i];
      const s = spans[i];
      const rect = (run.text && s.firstChild) ? (s.firstChild as any).getBoundingClientRect?.() ?? s.getBoundingClientRect() : s.getBoundingClientRect();
      const gKey = `${run.id}:0`;
      graphemeMap[gKey] = { lineIndex: 0, x: rect.x };
    }
    return { rectsByLine, graphemeMap };
  }

  // Full granularity: per-grapheme mapping
  for (let i = 0; i < items.length; i++) {
    const { run } = items[i];
    const span = spans[i];
    if (!run.text || run.text.length === 0) {
      // zero-width inline anchor
      const rect = measureZeroWidthAnchorRect(span);
      const centerY = rect.y - origin.top + rect.height / 2;
      const lineIdx = nearestLineIndex(rectsByLine, centerY);
      graphemeMap[`${run.id}:0`] = { lineIndex: lineIdx, x: rect.x - origin.left };
      continue;
    }
    const textNode = span.firstChild as Text;
    const graphemes = segmentGraphemes(run.text);
    let offset = 0;
    for (let gIndex = 0; gIndex < graphemes.length; gIndex++) {
      const g = graphemes[gIndex];
      const start = offset;
      const end = offset + g.length;
      offset = end;
      const r = document.createRange();
      try {
        r.setStart(textNode, start);
        r.setEnd(textNode, end);
      } catch {
        continue;
      }
      const rect = r.getBoundingClientRect();
      const centerY = rect.y - origin.top + rect.height / 2;
      const li = nearestLineIndex(rectsByLine, centerY);
      graphemeMap[`${run.id}:${gIndex}`] = { lineIndex: li, x: rect.x - origin.left };
    }
  }

  return { rectsByLine, graphemeMap };
}

function nearestLineIndex(lines: LineRect[], yCenter: number): number {
  if (!lines.length) return 0;
  let best = 0;
  let bestDist = Number.POSITIVE_INFINITY;
  for (let i = 0; i < lines.length; i++) {
    const r = lines[i];
    const top = r.y;
    const bottom = r.y + r.height;
    if (yCenter >= top && yCenter <= bottom) return i;
    const d = Math.min(Math.abs(yCenter - top), Math.abs(yCenter - bottom));
    if (d < bestDist) { bestDist = d; best = i; }
  }
  return best;
}



